import { useState, useCallback, useMemo } from "react";
import { getSecureItem, setSecureItem } from "utils/SecureStorage";
import { api } from "utils/Api";

const ROLE_STORAGE_KEY = "roles";
const ROLE_ID_KEY = "role_id";

// ✅ Canonical role keys (used internally throughout the app)
export const ROLE_KEYS = {
  SUPER_ADMIN: "super_admin",
  ADMIN: "admin",
  MANAGER: "manager",
  STAFF: "staff",
  TENANT: "tenant",
};

// ✅ Map API role names → internal keys (case-insensitive, flexible)
const normalizeRoleName = (name) => {
  if (!name) return null;
  const lower = name.toLowerCase().trim();

  // Handle variations of role names from API
  if (lower === "super admin" || lower === "super_admin" || lower === "superadmin") {
    return ROLE_KEYS.SUPER_ADMIN;
  }
  if (lower === "admin") {
    return ROLE_KEYS.ADMIN;
  }
  if (lower === "manager") {
    return ROLE_KEYS.MANAGER;
  }
  if (lower === "staff") {
    return ROLE_KEYS.STAFF;
  }
  if (lower === "tenant" || lower === "user") {
    return ROLE_KEYS.TENANT;
  }

  return null; // Unknown role
};

function useRole() {
  const [roles, setRoles] = useState(() => {
    const stored = getSecureItem(ROLE_STORAGE_KEY);
    try {
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [roleId, setRoleId] = useState(() =>
    Number(getSecureItem(ROLE_ID_KEY)) || 0
  );

  const [loading, setLoading] = useState(false);

  // ✅ Build a map: role_id → internal key (e.g., 3 → "manager")
  const roleIdToKeyMap = useMemo(() => {
    const map = {};
    roles.forEach((r) => {
      const key = normalizeRoleName(r.name);
      if (key && r.active) {
        map[r.id] = key;
      }
    });
    return map;
  }, [roles]);

  // ✅ Build reverse map: internal key → role_id (e.g., "manager" → 3)
  const keyToRoleIdMap = useMemo(() => {
    const map = {};
    Object.entries(roleIdToKeyMap).forEach(([id, key]) => {
      map[key] = Number(id);
    });
    return map;
  }, [roleIdToKeyMap]);

  // ✅ Get the internal role key for the current user
  const currentRoleKey = useMemo(() => {
    return roleIdToKeyMap[roleId] || null;
  }, [roleId, roleIdToKeyMap]);

  // ✅ Fetch & store roles from API
  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.roles.getRoles();
      const data = res?.data || res;

      const cleaned = data.map((r) => ({
        id: r.id,
        name: r.name,
        description: r.description,
        active: r.active,
      }));

      setRoles(cleaned);
      setSecureItem(ROLE_STORAGE_KEY, JSON.stringify(cleaned));
      return cleaned;
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshRoleId = useCallback(() => {
    setRoleId(Number(getSecureItem(ROLE_ID_KEY)) || 0);
  }, []);

  const getRoleById = useCallback(
    (id) => roles.find((r) => r.id === id && r.active),
    [roles]
  );

  const getRoleName = useCallback(
    (id) => getRoleById(id)?.name || "",
    [getRoleById]
  );

  // ✅ Get internal key for any role_id
  const getRoleKey = useCallback(
    (id) => roleIdToKeyMap[id] || null,
    [roleIdToKeyMap]
  );

  // ✅ Get role_id for any internal key
  const getRoleIdByKey = useCallback(
    (key) => keyToRoleIdMap[key] || null,
    [keyToRoleIdMap]
  );

  // ✅ Dynamic boolean flags
  const isSuperAdmin = useMemo(
    () => currentRoleKey === ROLE_KEYS.SUPER_ADMIN,
    [currentRoleKey]
  );
  const isAdmin = useMemo(
    () =>
      currentRoleKey === ROLE_KEYS.ADMIN ||
      currentRoleKey === ROLE_KEYS.SUPER_ADMIN,
    [currentRoleKey]
  );
  const isManager = useMemo(
    () => currentRoleKey === ROLE_KEYS.MANAGER,
    [currentRoleKey]
  );
  const isStaff = useMemo(
    () => currentRoleKey === ROLE_KEYS.STAFF,
    [currentRoleKey]
  );
  const isTenant = useMemo(
    () => currentRoleKey === ROLE_KEYS.TENANT,
    [currentRoleKey]
  );

  return {
    roles,
    loading,
    roleId,
    currentRoleKey,
    fetchRoles,
    refreshRoleId,
    getRoleById,
    getRoleName,
    getRoleKey,
    getRoleIdByKey,
    roleIdToKeyMap,
    keyToRoleIdMap,
    isSuperAdmin,
    isAdmin,
    isManager,
    isStaff,
    isTenant,
  };
}

export default useRole;